import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import JournalSection from '../../../components/journal/JournalSection/JournalSection'
import { HeaderColors } from '../../../components/types'
import JournalSectionProvider from '../../../contexts/journalSectionProvider'
import { initializeApollo } from '../../../lib/apolloClient'
import { getJournalSections } from '../../../lib/getJournalSections'
import globalCache from '../../../lib/globalCache'
import { JOURNAL_ITEMS } from '../../../queries/journalQuery'
import { SortOrder } from '../../../__generated__/globalTypes'
import {
  journalItems,
  journalItemsVariables,
} from '../../../__generated__/journalItems'

const JournalIndexPage: NextPage<{ section: string; today: string }> = ({
  section,
  today,
}) => {
  return (
    <JournalSectionProvider section={section} today={today}>
      <JournalSection />
    </JournalSectionProvider>
  )
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const apolloClient = initializeApollo()
  const today = new Date().toISOString()
  await globalCache(apolloClient)

  const [sections, items] = await Promise.all([
    getJournalSections(apolloClient),
    apolloClient.query<journalItems, journalItemsVariables>({
      query: JOURNAL_ITEMS,
      variables: {
        where: {
          iblock: { code: { equals: 'articles' } },
          sections: { some: { code: { equals: params.section as string } } },
          active: { equals: true },
          OR: [
            {
              activeFrom: { lte: today },
              activeTo: { equals: null },
            },
            {
              activeFrom: { lte: today },
              activeTo: { gte: today },
            },
            {
              activeFrom: { equals: null },
              activeTo: { equals: null },
            },
            {
              activeFrom: { equals: null },
              activeTo: { gte: today },
            },
          ],
        },
        first: 6,
        order: { activeFrom: SortOrder.desc },
      },
    }),
  ])

  if (items.data?.elements?.edges?.length < 1) {
    return { notFound: true, revalidate: 60 }
  }

  const currentSection = sections?.data?.sections?.nodes?.filter(
    (node) => node.code === params.section,
  )[0]

  const seo = {
    title: currentSection?.seo?.sectionMetaTitle,
    description: currentSection?.seo?.sectionMetaDescription,
    keywords: currentSection?.seo?.sectionMetaKeywords,
  }

  return {
    props: {
      __APOLLO_STATE__: apolloClient.cache.extract(),
      headerInitColor: HeaderColors.white,
      section: params.section,
      seo,
      today,
    },
    revalidate: 60,
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths: { params: { section: string; path: string[] } }[] = []

  return {
    paths,
    fallback: 'blocking',
  }
}

export default JournalIndexPage
